import type {
  ApiLanguage, CachedLangData, LangPack, LangPackStringValuePlural,
} from '../../api/types';

import readStrings from './readStrings';

export const BUILT_IN_LANGUAGES: ApiLanguage[] = [
  {
    langCode: 'en',
    name: 'English',
    nativeName: 'English',
    pluralCode: 'en',
    stringsCount: 3181,
    translatedCount: 3181,
    translationsUrl: 'https://teamgram.net/translations/en/weba',
    isOfficial: true,
  },
  {
    langCode: 'zh-hans',
    name: 'Chinese (Simplified)',
    nativeName: '简体中文',
    pluralCode: 'zh',
    baseLangCode: 'zh-hans',
    stringsCount: 3181,
    translatedCount: 3181,
    translationsUrl: 'https://teamgram.net/translations/zh-hans/weba',
    isOfficial: true,
  },
  {
    langCode: 'zh-hant',
    name: 'Chinese (Traditional)',
    nativeName: '繁體中文',
    pluralCode: 'zh',
    baseLangCode: 'zh-hant',
    stringsCount: 3181,
    translatedCount: 3181,
    translationsUrl: 'https://teamgram.net/translations/zh-hant/weba',
    isOfficial: true,
  },
];

export function normalizeLangCode(langCode: string): string {
  const code = (langCode || 'en').toLowerCase().replace('_', '-');
  if (code === 'zh' || code === 'zh-cn' || code === 'zh-sg' || code === 'zh-hans') {
    return 'zh-hans';
  }
  if (code === 'zh-tw' || code === 'zh-hk' || code === 'zh-mo' || code === 'zh-hant') {
    return 'zh-hant';
  }
  return code;
}

export function isBuiltInLanguage(langCode: string): boolean {
  const normalized = normalizeLangCode(langCode);
  return BUILT_IN_LANGUAGES.some((lang) => lang.langCode === normalized);
}

export function getBuiltInLanguage(langCode: string): ApiLanguage | undefined {
  const normalized = normalizeLangCode(langCode);
  return BUILT_IN_LANGUAGES.find((lang) => lang.langCode === normalized);
}

export function parseLangPackStrings(fileData: string, langCode: string): CachedLangData {
  const rawStrings = readStrings(fileData);
  const strings: LangPack['strings'] = {};

  Object.entries(rawStrings).forEach(([key, value]) => {
    const [clearKey, pluralSuffix] = key.split('_');

    if (!pluralSuffix) {
      strings[clearKey] = value;
      return;
    }

    const knownValue = (strings[clearKey] || {}) as LangPackStringValuePlural;
    knownValue[pluralSuffix as keyof LangPackStringValuePlural] = value;
    strings[clearKey] = knownValue;
  });

  const langPack: LangPack = {
    langCode,
    version: 0,
    strings,
  };

  const langInfo = getBuiltInLanguage(langCode) || {
    langCode,
    name: langCode,
    nativeName: langCode,
    pluralCode: langCode,
    stringsCount: Object.keys(strings).length,
    translatedCount: Object.keys(strings).length,
    translationsUrl: `https://teamgram.net/translations/${langCode}/weba`,
  };

  return {
    langPack,
    language: langInfo,
  };
}

export async function readBuiltInLangPack(langCode: string): Promise<CachedLangData> {
  const normalized = normalizeLangCode(langCode);

  let fileData: string;
  if (normalized === 'zh-hans') {
    const file = await import('../../assets/localization/zh-hans.strings');
    fileData = file.default;
  } else if (normalized === 'zh-hant') {
    const file = await import('../../assets/localization/zh-hant.strings');
    fileData = file.default;
  } else {
    const file = await import('../../assets/localization/fallback.strings');
    fileData = file.default;
  }

  return parseLangPackStrings(fileData, normalized);
}
